import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

/**
 * Modal — shared dialog primitive (A11Y-006 from the 2026-05-03 audit;
 * promoted from `bible/BibleModalShell.jsx` so the chat / admin / shared
 * surfaces can reach it too). The Bible Reader's overlays still import
 * it via the existing `BibleModalShell` re-export — no Bible callsites
 * need to move.
 *
 * Responsibilities (single source of truth so each overlay can stop
 * reimplementing half-working dialog semantics):
 *  - role="dialog" + aria-modal="true" + aria-labelledby wired to the
 *    caller-provided `titleId` (caller renders the <h*> / <p id={titleId}>
 *    inside `children` so the title stays in the caller's markup)
 *  - aria-describedby wired to caller-provided `describedBy` for body
 *    text (paragraphs, instructions). Optional.
 *  - Escape key closes (calls `onClose`)
 *  - Focus trap: Tab / Shift-Tab cycles among focusables inside the dialog
 *  - Focus return: focus goes back to whatever was focused before the
 *    dialog opened (falls back to document.body if that element is gone)
 *  - Body scroll lock while open
 *  - Overlay click → close (optional via `closeOnOverlayClick`, default true)
 *  - Initial focus: `initialFocusRef.current` if provided, else the first
 *    focusable element inside the dialog, else the dialog container itself
 *
 * Visual layout is the caller's job — this primitive holds NO opinion on
 * size, spacing, color, or animation. The caller passes a `className` that
 * styles the inner dialog container (so e.g. the mobile insight sheet can
 * keep its Framer Motion slide-up).
 */
function getFocusable(container) {
  if (!container) return []
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')
  return Array.from(container.querySelectorAll(selector)).filter(
    el => !el.hasAttribute('aria-hidden') && el.offsetParent !== null
  )
}

function Modal({
  open,
  onClose,
  titleId,
  describedBy,
  children,
  closeOnOverlayClick = true,
  initialFocusRef,
  className = '',
  overlayClassName = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50',
  style,
  lockBodyScroll = true,
}) {
  const dialogRef = useRef(null)
  const previouslyFocusedRef = useRef(null)

  useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = typeof document !== 'undefined' ? document.activeElement : null

    const prevOverflow = document.body.style.overflow
    if (lockBodyScroll) document.body.style.overflow = 'hidden'

    const container = dialogRef.current
    const explicit = initialFocusRef?.current
    if (explicit && typeof explicit.focus === 'function') {
      explicit.focus()
    } else if (container) {
      const focusables = getFocusable(container)
      if (focusables.length > 0) {
        focusables[0].focus()
      } else {
        container.focus()
      }
    }

    return () => {
      if (lockBodyScroll) document.body.style.overflow = prevOverflow
      const prev = previouslyFocusedRef.current
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus()
      } else if (typeof document !== 'undefined' && document.body) {
        document.body.focus?.()
      }
    }
  }, [open, initialFocusRef, lockBodyScroll])

  useEffect(() => {
    if (!open) return
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose?.()
        return
      }
      if (e.key !== 'Tab') return
      const container = dialogRef.current
      if (!container) return
      const focusables = getFocusable(container)
      if (focusables.length === 0) {
        e.preventDefault()
        container.focus()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [open, onClose])

  if (!open) return null
  if (typeof document === 'undefined') return null

  function handleOverlayClick() {
    if (closeOnOverlayClick) onClose?.()
  }

  return createPortal(
    <div className={overlayClassName} onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={className}
        style={style}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

Modal.displayName = 'Modal'

export default Modal
